import type { ModuleName } from '@contexts/modules-provider';

import { useCallback, useRef } from 'react';

import type {
  DeepLinkResult,
  UseDeepLinkNavigationOptions,
  UseDeepLinkNavigationReturn,
} from './useDeepLinkNavigation.types';

const DEEP_LINK_KEY = 'auth-deep-link';

/**
 * Parses module name from a deep link URL
 * @param url - The deep link URL path
 * @returns Module name or null for common flow
 */
function parseModuleFromUrl(url: string): ModuleName | null {
  if (url.startsWith('/train')) return 'Train';
  if (url.startsWith('/instruct')) return 'Instruct';
  if (url.startsWith('/manage')) return 'Manage';
  return null;
}

/**
 * Parses the customer ID from a Manage deep link URL.
 * Expected format: /manage/:customerId/...
 * @param url - The deep link URL path
 * @returns The customer ID or null if the URL format is invalid
 */
function parseCustomerIdFromManageUrl(url: string): string | null {
  const segments = url.split('/').filter(Boolean);
  if (segments.length >= 2 && segments[0] === 'manage') {
    return segments[1];
  }
  return null;
}

/**
 * Hook for handling deep link navigation logic
 *
 * Responsibilities:
 * - Parse deep link from sessionStorage
 * - Determine target module from URL
 * - Validate module access
 * - Return navigation decisions
 *
 * @example
 * ```tsx
 * const { processDeepLink, clearDeepLink, hasDeepLink } = useDeepLinkNavigation({
 *   modules,
 *   selectedModule,
 *   organizations,
 *   selectedOrganization,
 *   isModulesLoading,
 *   isOrganizationsLoading,
 * });
 *
 * const result = processDeepLink();
 * if (result.status === 'navigate') {
 *   navigate(result.path, { replace: true });
 * }
 * ```
 */
export function useDeepLinkNavigation(
  options: UseDeepLinkNavigationOptions
): UseDeepLinkNavigationReturn {
  const {
    modules,
    selectedModule,
    selectedOrganization,
    isModulesLoading,
    isOrganizationsLoading,
  } = options;

  // Read deep link once and cache in ref. sessionStorage is only set
  // before MSAL redirect, so it won't change while the hook is alive.
  const deepLinkRef = useRef<string | null>(
    sessionStorage.getItem(DEEP_LINK_KEY)
  );
  const deepLinkUrl = deepLinkRef.current;

  const hasDeepLink = Boolean(deepLinkUrl);

  const clearDeepLink = useCallback((): void => {
    sessionStorage.removeItem(DEEP_LINK_KEY);
    deepLinkRef.current = null;
  }, []);

  const processDeepLink = useCallback((): DeepLinkResult => {
    return processDeepLinkResult({
      deepLinkUrl,
      modules,
      selectedModule,
      selectedOrganization,
      organizations: options.organizations,
      isModulesLoading,
      isOrganizationsLoading,
    });
  }, [
    deepLinkUrl,
    isModulesLoading,
    isOrganizationsLoading,
    modules,
    selectedModule,
    selectedOrganization,
    options.organizations,
  ]);

  return {
    processDeepLink,
    clearDeepLink,
    hasDeepLink,
    deepLinkUrl,
  };
}

/**
 * Pure function extracted from processDeepLink to reduce cognitive complexity.
 * Determines the next action based on deep link URL and current state.
 */
interface ProcessDeepLinkParams {
  deepLinkUrl: string | null;
  modules: UseDeepLinkNavigationOptions['modules'];
  selectedModule: UseDeepLinkNavigationOptions['selectedModule'];
  selectedOrganization: UseDeepLinkNavigationOptions['selectedOrganization'];
  organizations: UseDeepLinkNavigationOptions['organizations'];
  isModulesLoading: boolean;
  isOrganizationsLoading: boolean;
}

function processDeepLinkResult(params: ProcessDeepLinkParams): DeepLinkResult {
  const {
    deepLinkUrl,
    modules,
    selectedModule,
    selectedOrganization,
    organizations,
    isModulesLoading,
    isOrganizationsLoading,
  } = params;

  if (!deepLinkUrl) {
    return { status: 'no-deep-link' };
  }

  if (isModulesLoading) {
    return { status: 'waiting-for-modules' };
  }

  const deepLinkModule = parseModuleFromUrl(deepLinkUrl);

  // Common flow (no module in URL) — navigate directly
  if (!deepLinkModule) {
    return { status: 'navigate', path: deepLinkUrl };
  }

  return processModuleDeepLink({
    deepLinkUrl,
    deepLinkModule,
    modules,
    selectedModule,
    selectedOrganization,
    organizations,
    isOrganizationsLoading,
  });
}

/**
 * Handles module-specific deep link processing (extracted to reduce complexity).
 */
function processModuleDeepLink(params: {
  deepLinkUrl: string;
  deepLinkModule: ModuleName;
  modules: ProcessDeepLinkParams['modules'];
  selectedModule: ProcessDeepLinkParams['selectedModule'];
  selectedOrganization: ProcessDeepLinkParams['selectedOrganization'];
  organizations: ProcessDeepLinkParams['organizations'];
  isOrganizationsLoading: boolean;
}): DeepLinkResult {
  const {
    deepLinkUrl,
    deepLinkModule,
    modules,
    selectedModule,
    selectedOrganization,
    organizations,
    isOrganizationsLoading,
  } = params;

  const targetModule = modules.find(m => m.name === deepLinkModule);
  if (!targetModule) {
    return { status: 'unauthorized' };
  }

  if (selectedModule?.name !== targetModule.name) {
    return { status: 'select-module', moduleName: targetModule.name };
  }

  if (deepLinkModule === 'Manage') {
    return processManageDeepLink({
      deepLinkUrl,
      selectedOrganization,
      organizations,
      isOrganizationsLoading,
    });
  }

  return { status: 'navigate', path: deepLinkUrl };
}

/**
 * Handles Manage module deep link organization validation (extracted to reduce complexity).
 */
function processManageDeepLink(params: {
  deepLinkUrl: string;
  selectedOrganization: ProcessDeepLinkParams['selectedOrganization'];
  organizations: ProcessDeepLinkParams['organizations'];
  isOrganizationsLoading: boolean;
}): DeepLinkResult {
  const {
    deepLinkUrl,
    selectedOrganization,
    organizations,
    isOrganizationsLoading,
  } = params;

  // SF/ETCA redirect bypasses org-URL validation — the redirect page handles org resolution itself.
  if (
    deepLinkUrl.startsWith('/manage/sf-redirect') ||
    deepLinkUrl.startsWith('/manage/etca-redirect')) {
    return { status: 'navigate', path: deepLinkUrl };
  }

  if (isOrganizationsLoading) {
    return { status: 'waiting-for-orgs' };
  }

  const deepLinkCustomerId = parseCustomerIdFromManageUrl(deepLinkUrl);
  if (!deepLinkCustomerId) {
    return { status: 'unauthorized' };
  }

  const targetOrg = organizations.find(
    org => org.customerId === deepLinkCustomerId
  );
  if (!targetOrg) {
    return { status: 'unauthorized' };
  }

  if (selectedOrganization?.customerId !== deepLinkCustomerId) {
    return { status: 'select-org', orgId: targetOrg.id };
  }

  return { status: 'navigate', path: deepLinkUrl };
}
